import React, { useState, useMemo, useRef } from "react";
import Button from "components/Elements/Button/Button.js";
import GetTransformedDate from "utils/dateViewTransformer.tsx";
import apiRequest from "utils/ApiRequest/apiRequest.js";
import Icon from "components/Elements/Icon/Icon.tsx";
import parse from "html-react-parser";
import UploadHeaderLogo from "./UploadHeader/UploadHeaderLogo.js";
import BrandingRemoveModal from "../../BrandingRemoveModal.js";
import Tooltip from "components/Elements/Tooltip/Tooltip.js";
import SignatureText from "./SignatureText.js";
import useMediaQueries from "hooks/useMediaQueries.js";
import JImage from "components/Elements/Image/Image.tsx";
import { useUser } from "contexts/userContext/userContext.js";
import { useToasts } from "react-toast-notifications";
import { useContent } from "contexts/contentContext/contentContext.js";
import { useModal } from "contexts/modalContext/modalContext.js";
import { jumbomailImagesSizeLimit } from "utils/jumbomailSettings.js";
import { useRateLimitError } from "hooks/useRateLimitError.ts";
import config from "config.ts";

import "./BrandingEmail.scss";

const jumbomailLogo = `${config.api.resource}/assets/images/jumbomail-logo-black.svg`;

const fileReader = new FileReader();

const BrandingEmail = () => {
  const { newMobileCondition } = useMediaQueries();
  const { getTextByKey } = useContent();
  const { getUserData, isSubscribed } = useUser();
  const { addToast } = useToasts();
  const { setModal, closeModal } = useModal();
  const userData = getUserData();
  const [emailHeader, setEmailHeader] = useState("first");
  const [signatureText, setSignatureText] = useState("");
  const [oldSignature, setOldSignature] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [signatureLogo, setSignatureLogo] = useState(null);
  const [imageName, setImageName] = useState("");
  const [inputImage, setInputImage] = useState(null);
  const canvasRef = useRef();
  const footer = getTextByKey("email-design-footer");
  const exampleFilesList = ["example.jpg", "example2.png"];
  const [validFile, setValidFile] = useState(true);
  const { isRateLimitError, rateLimitError } = useRateLimitError();
  useMemo(async () => {
    //GetUserSignatureDetails
    const response = await apiRequest("Users", "GetUserSignatureDetails");
    if (response.status === "success") {
      if (response.data.SignatureText) {
        let tempSignature = response.data.SignatureText.includes("&#34;")
          ? response.data.SignatureText.replace(/&#34;/g, '"')
          : response.data.SignatureText;
        tempSignature = response.data.SignatureText;
        setSignatureText(tempSignature);
        setOldSignature(tempSignature);
      }
      if (response.data.SignatureLogo) {
        setSignatureLogo(response.data.SignatureLogo);
      }
      if (response.data.SignatureHeaderLogo) {
        setEmailHeader(response.data.SignatureHeaderLogo);
      } else {
        setEmailHeader(null);
      }
    } else {
    }
  }, []);

  const updateSignature = async (signature) => {
    setEditMode(false);

    setSignatureText(signature);
    const body = {
      SignatureText: signature,
    };
    const response = await apiRequest(
      "UserTemplates",
      "CreateOrUpdateUserMailTemplateReactSignature",
      body,
      "POST"
    );
    if (response.status === "success") {
      setEditMode(false);
      addToast(getTextByKey("update-signature-success"), {
        appearance: "success",
      });
    } else {
    }
  };

  const deleteSignature = async () => {
    const response = await apiRequest(
      "UserTemplates",
      "DeleteUserSignatureNoHeaderReact"
    );
    if (response.status === "success") {
      setSignatureText("");
      closeModal();
      addToast(getTextByKey("delete-signature-success"), {
        appearance: "success",
      });
    } else {
    }
  };

  const displayErrorModal = () => {
    addToast(getTextByKey("feature-availble-only-subs"), {
      appearance: "error",
    });
  };

  const handleChangeSignatureLogo = (e) => {
    if (!isSubscribed()) {
      addToast(getTextByKey("feature-availble-only-subs"), {
        appearance: "error",
      });
    } else {
      let validFile = true;
      let textValidation = "";
      const image = e.target.files[0];
      const allowedExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];
      //get the extension of the file
      const fileExtension = image.name.split(".").pop();
      //check the size of image is less than 2MB
      if (image.size > jumbomailImagesSizeLimit["signatureLogo"]) {
        validFile = false;
        textValidation = getTextByKey("embed-logo-validation-size-text");
      }
      //check the extension of image is in allowedExtensions
      if (!allowedExtensions.includes(fileExtension)) {
        validFile = false;
        textValidation = getTextByKey("file-type-not-supported");
      }

      setValidFile(validFile);
      if (validFile) {
        setInputImage(image);
        fileReader.readAsDataURL(image);
        const fileName = image.name.replaceAll("&", "-");
        setImageName(fileName);
        //uploadImageSignature(fileName, image);
      } else {
        addToast(textValidation, {
          appearance: "error",
        });
      }
    }
  };

  fileReader.onload = function (event) {
    if (validFile) {
      var image = new Image();
      image.onload = function () {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        const ratio = 1;
        canvas.width = image.width / ratio;
        canvas.height = image.height / ratio;
        context.drawImage(
          image,
          0,
          0,
          image.width,
          image.height,
          0,
          0,
          canvas.width,
          canvas.height
        );
        uploadImageSignature(imageName, inputImage);
      };
      image.src = event.target.result;
    }
  };

  const uploadImageSignature = async (fileName, image) => {
    const response = await apiRequest(
      "UserTemplates",
      `UpdateSignatureLogoReact?fileName=${fileName}&fileId=${0}`,
      image,
      "POST",
      null,
      null,
      null,
      true
    );
    if (response.status === "success") {
      if (response.data) {
        setSignatureLogo(response.data.path);
      }
    } else {
      let error = getTextByKey("image-size-error");
      if (isRateLimitError(response)) error = rateLimitError;

      addToast(error, {
        appearance: "error",
      });
    }
  };

  const deleteSignatureLogo = async () => {
    const response = await apiRequest(
      "UserTemplates",
      "DeleteUserSignatureLogoReact"
    );
    if (response.status === "success") {
      setSignatureLogo(null);
      closeModal();
      addToast(getTextByKey("delete-signature-logo-success"), {
        appearance: "success",
      });
    } else {
    }
  };

  const removeImageApi = () => {};

  const removeImageHeader = async () => {
    const response = await apiRequest(
      "UserTemplates",
      "DeleteUserSignatureHeaderLogoReact"
    );
    if (response.status === "success") {
      setEmailHeader(null);
      closeModal();
      addToast(getTextByKey("delete-signature-logo-header-success"), {
        appearance: "success",
      });
    } else {
    }
  };

  const openUploadHeader = () => {
    if (isSubscribed()) {
      setModal(() => (
        <UploadHeaderLogo
          pcImage={true}
          LogoName={null}
          logoImage={emailHeader ? emailHeader : null}
          setLogoImage={setEmailHeader}
          removeImageApi={removeImageApi}
        />
      ));
    } else {
      addToast(getTextByKey("feature-availble-only-subs"), {
        appearance: "error",
      });
    }
  };

  const openRemoveLogoModal = () => {
    setModal(() => (
      <BrandingRemoveModal
        titleModal={getTextByKey("remove-signature-header-logo")}
        handleClick={removeImageHeader}
      />
    ));
  };

  const openRemoveSignatureTextModal = () => {
    setModal(() => (
      <BrandingRemoveModal
        titleModal={getTextByKey("remove-signature-text")}
        handleClick={deleteSignature}
      />
    ));
  };

  const openRemoveSignatureLogoModal = () => {
    setModal(() => (
      <BrandingRemoveModal
        titleModal={getTextByKey("remove-signature-logo")}
        handleClick={deleteSignatureLogo}
      />
    ));
  };

  const fixUntilDownloadText = (text) => {
    let tempText = text;
    let ExpirationDate = GetTransformedDate({
      date: new Date(),
      fullView: true,
      showDay: false,
    });
    if (text.includes("#ExpirationDate#")) {
      tempText = text.replace("#ExpirationDate#", ExpirationDate);
    }
    return tempText;
  };

  const fixSentItemsText = (text) => {
    let tempText = text;
    if (text.includes(`(#FilesSize#, #NumberOfFiles#)`)) {
      tempText = tempText.replace(
        `(#FilesSize#, #NumberOfFiles#)`,
        '<span class="sent-files-details">(100MB, 2)</span>'
      );
    }
    return tempText;
  };

  const editingDisabled = getUserData().TopUserBrandingEnabled;

  return (
    <>
      {/* {!isSubscribed() && (
        <OnlySubscribersAlert text={"branding-email-only-subscriber-text"} />
      )} */}
      <div className="branding-email-page">
        <div className={`email-header ${!emailHeader ? "default-logo" : ""}`}>
          {emailHeader !== "first" && (
            <JImage
              src={emailHeader ? emailHeader : jumbomailLogo}
              alt=""
              title=""
            />
          )}
          <div className="actions">
            <div className="upload-image">
              {editingDisabled ? (
                <Tooltip tooltipTextKey="brandign-not-enabled">
                  <div className="branding-disabled-icon">
                    <Icon name="info" />
                  </div>
                </Tooltip>
              ) : (
                <Tooltip
                  id="upload-header-logo-signature-tooltip"
                  tooltipTextKey="upload-header-logo-signature-tooltip"
                  end={!newMobileCondition ? true : false}
                  bottom={newMobileCondition ? true : false}
                >
                  <Icon name="camera" onClick={openUploadHeader} />
                </Tooltip>
              )}
            </div>
            {emailHeader && !editingDisabled && (
              <div className="remove-image">
                <Tooltip
                  id="remove-header-logo-signature-tooltip"
                  tooltipTextKey="remove-header-logo-signature-tooltip"
                  end={!newMobileCondition ? true : false}
                  bottom={newMobileCondition ? true : false}
                >
                  <Icon
                    name="trash-solid"
                    onClick={() => openRemoveLogoModal()}
                  />
                </Tooltip>
              </div>
            )}
          </div>
        </div>
        <div className="email-content">
          <div className="email-content-header">
            <span className="user-email">{userData.UserName}</span>
            <span className="sent-you-text">
              {getTextByKey("email-design-sent-you-some-files")}
            </span>
            <Button>{getTextByKey("email-design-download-your-files")}</Button>
            <span className="until-text">
              {fixUntilDownloadText(
                getTextByKey("email-design-download-until")
              )}
            </span>
          </div>
          <div className="email-download-content">
            <div className="download-link part">
              <h3>{getTextByKey("email-design-download-link")}</h3>
              <a href="https://www.jumbomail.me">https://www.jumbomail.me</a>
            </div>
            <div className="sent-items part">
              <h3>
                {parse(
                  fixSentItemsText(getTextByKey("email-design-sent-items"))
                )}
              </h3>
              {exampleFilesList.map((item, i) => (
                <span key={i}>{item}</span>
              ))}
            </div>
            <div className="mail-message part">
              <h3>{getTextByKey("email-design-mail-message")}</h3>
              <span className="subject">
                {getTextByKey("email-design-example-subject")}
              </span>
              <span className="message">
                {getTextByKey("email-design-example-message")}
              </span>
            </div>
          </div>
          <div className="siganture-content">
            <SignatureText
              setSignatureText={setSignatureText}
              signatureText={signatureText}
              updateSignature={updateSignature}
              openRemoveSignatureTextModal={openRemoveSignatureTextModal}
              editMode={editMode}
              setEditMode={setEditMode}
            />
            <div className="signature-logo">
              <div
                className={`signature-image ${
                  !signatureLogo ? "empty-image" : ""
                }`}
              >
                {signatureLogo && (
                  <JImage src={signatureLogo} alt="" title="" />
                )}
              </div>
              <div
                className={`actions ${
                  !signatureLogo ? "empty-logo-actions" : ""
                }`}
              >
                {editingDisabled ? (
                  <Tooltip tooltipTextKey="brandign-not-enabled">
                    <div className="branding-disabled-icon">
                      <Icon name="info" />
                    </div>
                  </Tooltip>
                ) : !signatureLogo ? (
                  <div className="upload-image">
                    <label htmlFor="upload-image-signature">
                      <Icon name="camera" />
                    </label>
                    <input
                      onChange={handleChangeSignatureLogo}
                      id="upload-image-signature"
                      name="upload-image-signature"
                      type="file"
                      alt=""
                      title=""
                      ref={canvasRef}
                    />
                    <Tooltip
                      id="upload-image-signature-tooltip"
                      tooltipTextKey="upload-image-signature-tooltip"
                      end={!newMobileCondition ? true : false}
                      bottom={newMobileCondition ? true : false}
                    >
                      <div className={`tootltip-icon-container`}>
                        <Icon
                          name="question-circle"
                          iconClass="tooltip-question"
                        />
                      </div>
                    </Tooltip>
                  </div>
                ) : (
                  <>
                    <div
                      className="delete"
                      onClick={() => openRemoveSignatureLogoModal()}
                    >
                      <Tooltip
                        id="remove-signature-logo-tooltip"
                        tooltipTextKey="remove-signature-logo-tooltip"
                        end={!newMobileCondition ? true : false}
                        bottom={newMobileCondition ? true : false}
                      >
                        <Icon name="trash-solid" />
                      </Tooltip>
                    </div>
                    <Tooltip
                      id="upload-image-signature-tooltip"
                      tooltipTextKey="upload-image-signature-tooltip"
                      end={!newMobileCondition ? true : false}
                      bottom={newMobileCondition ? true : false}
                    >
                      <div className="upload-image">
                        <label htmlFor="upload-image-signature">
                          <Icon name="camera" />
                        </label>
                        <input
                          onChange={handleChangeSignatureLogo}
                          id="upload-image-signature"
                          name="upload-image-signature"
                          type="file"
                          alt=""
                          title=""
                          ref={canvasRef}
                        />
                      </div>
                    </Tooltip>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="footer-content">{parse(footer)}</div>
      </div>
    </>
  );
};

export default BrandingEmail;
